import { useLanguage } from '@/contexts/language-context'

/**
 * Private helper to format a date with locale-aware formatting
 * @param date - The date to format (can be null/undefined)
 * @param options - Intl.DateTimeFormat options
 * @param locale - The locale to use for formatting
 * @param fallback - Fallback string for null/undefined dates
 * @returns Formatted date string or fallback for null/undefined
 */
function formatDateInternal(
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale: string = 'en-CA',
  fallback: string = '—'
): string {
  if (!date) return fallback

  let d: Date
  if (date instanceof Date) {
    d = date
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // Date-only string ("YYYY-MM-DD") is a timezone-agnostic calendar date. `new Date(str)` parses
    // it as UTC midnight, which then renders as the PREVIOUS day in negative-offset timezones
    // (e.g. "2026-07-01" -> "Jun 30"). Build it from local components so the calendar day is kept.
    const [year, month, day] = date.split('-').map(Number)
    d = new Date(0)
    d.setHours(0, 0, 0, 0)
    d.setFullYear(year, month - 1, day)
    // Component construction rolls invalid values over (e.g. 2026-13-01 -> 2027-01-01); reject those
    // so they hit the fallback instead of rendering a wrong-but-valid date.
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      return fallback
    }
  } else {
    // Full timestamps (with a time/offset) are real instants — parse as-is and convert to local.
    d = new Date(date)
  }
  if (isNaN(d.getTime())) return fallback

  return d.toLocaleString(locale, options)
}

/**
 * Format a date with locale-aware formatting (standard date format)
 * @param date - The date to format (can be null/undefined)
 * @param locale - The locale to use for formatting
 * @param fallback - Fallback string for null/undefined dates
 * @returns Formatted date string or fallback for null/undefined
 */
export function formatDate(
  date: Date | string | null | undefined,
  locale: string = 'en-CA',
  fallback: string = '—'
): string {
  return formatDateInternal(date, { year: 'numeric', month: 'short', day: 'numeric' }, locale, fallback)
}

/**
 * Format a date with time (timestamp) with locale-aware formatting (standard timestamp format)
 * @param date - The date to format (can be null/undefined)
 * @param locale - The locale to use for formatting
 * @param fallback - Fallback string for null/undefined dates
 * @returns Formatted timestamp string or fallback for null/undefined
 */
function formatTimestamp(
  date: Date | string | null | undefined,
  locale: string = 'en-CA',
  fallback: string = '—'
): string {
  return formatDateInternal(date, { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric', 
    hour: 'numeric', 
    minute: '2-digit' 
  }, locale, fallback)
}

/**
 * Format a time with locale-aware formatting (standard time format)
 * @param date - The date to format (can be null/undefined)
 * @param locale - The locale to use for formatting
 * @param fallback - Fallback string for null/undefined dates
 * @returns Formatted time string or fallback for null/undefined
 */
function formatTime(
  date: Date | string | null | undefined,
  locale: string = 'en-CA',
  fallback: string = '—'
): string {
  return formatDateInternal(date, { 
    hour: 'numeric', 
    minute: '2-digit' 
  }, locale, fallback)
}

/**
 * Format a date as month and year (e.g., "Jan 2024")
 * @param date - The date to format (can be null/undefined)
 * @param locale - The locale to use for formatting
 * @param fallback - Fallback string for null/undefined dates
 * @returns Formatted month/year string or fallback for null/undefined
 */
function formatMonthYear(
  date: Date | string | null | undefined,
  locale: string = 'en-CA',
  fallback: string = '—'
): string {
  return formatDateInternal(date, { year: 'numeric', month: 'short' }, locale, fallback)
}

/**
 * Format a number with locale-aware formatting
 * @param amount - The amount to format (can be null/undefined)
 * @param options - Intl.NumberFormat options
 * @param locale - The locale to use for formatting
 * @returns Formatted number string or "—" for null/undefined
 */
function formatNumber(
  amount: number | null | undefined,
  options: Intl.NumberFormatOptions = {},
  locale: string = 'en-CA'
): string {
  if (amount === null || amount === undefined) {
    return "—"
  }
  
  return new Intl.NumberFormat(locale, options).format(amount)
}

/**
 * Format a number as currency or decimal number with locale-aware formatting
 * @param amount - The amount to format (can be null/undefined)
 * @param currency - Optional currency code (e.g., 'CAD', 'USD'). If not provided, formats as a number with 2 decimal places.
 * @param locale - The locale to use for formatting
 * @returns Formatted currency string (e.g., "$1,234.56"), number string (e.g., "1,234.56"), or "—" for null/undefined
 */
function formatCurrency(
  amount: number | null | undefined,
  currency?: string,
  locale: string = 'en-CA'
): string {
  if (currency) {
    return formatNumber(amount, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }, locale)
  } else {
    // Fall back to number formatting without currency symbol
    return formatNumber(amount, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }, locale)
  }
}

/**
 * Format a number as a money value prefixed with "$". Handles negatives by
 * placing the sign before the symbol (e.g. "-$1,234.56" rather than
 * "$-1,234.56"). Independent of any specific currency code — uses the
 * locale's number formatting and prepends the literal "$" character.
 * @param amount - The amount to format (can be null/undefined)
 * @param locale - The locale to use for formatting
 * @param fallback - Fallback string for null/undefined amounts
 * @returns "$1,234.56", "-$1,234.56", or fallback for null/undefined
 */
function formatMoney(
  amount: number | null | undefined,
  locale: string = 'en-CA',
  fallback: string = '—'
): string {
  if (amount === null || amount === undefined) return fallback
  if (amount < 0) return `-$${formatCurrency(-amount, undefined, locale)}`
  return `$${formatCurrency(amount, undefined, locale)}`
}

/**
 * Hook that provides locale-aware formatters using the current language context
 * @returns An object containing all formatter functions
 */
export function useFormatters() {
  const { language } = useLanguage()
  
  return {
    /**
     * Format date values with proper locale (standard date format)
     * @param date - The date to format
     * @param fallback - Fallback string for invalid dates
     */
    formatDate: (date: Date | string | null | undefined, fallback?: string) => {
      return formatDate(date, language, fallback)
    },

    /**
     * Format timestamp (date + time) values with proper locale (standard timestamp format)
     * @param date - The date to format
     * @param fallback - Fallback string for invalid dates
     */
    formatTimestamp: (date: Date | string | null | undefined, fallback?: string) => {
      return formatTimestamp(date, language, fallback)
    },

    /**
     * Format time values with proper locale (standard time format)
     * @param date - The date to format
     * @param fallback - Fallback string for invalid dates
     */
    formatTime: (date: Date | string | null | undefined, fallback?: string) => {
      return formatTime(date, language, fallback)
    },

    /**
     * Format currency values with proper locale and currency symbol
     * @param amount - The amount to format
     * @param currency - Optional currency code (e.g., 'CAD', 'USD')
     */
    formatCurrency: (amount: number | null | undefined, currency?: string) => {
      return formatCurrency(amount, currency, language)
    },

    /**
     * Format a money value with "$" prefix. Handles negatives by placing the
     * sign before the symbol (e.g. "-$1,234.56" rather than "$-1,234.56").
     * @param amount - The amount to format
     * @param fallback - Fallback string for null/undefined amounts
     */
    formatMoney: (amount: number | null | undefined, fallback?: string) => {
      return formatMoney(amount, language, fallback)
    },

    /**
     * Format decimal values with specified decimal places
     * @param amount - The amount to format
     * @param decimals - Number of decimal places (defaults to 2)
     * @param minDecimals - Minimum decimal places to show (defaults to same as decimals)
     */
    formatDecimal: (amount: number | null | undefined, decimals: number = 2, minDecimals?: number) => {
      return formatNumber(amount, {
        minimumFractionDigits: minDecimals ?? decimals,
        maximumFractionDigits: decimals,
      }, language)
    },

    /**
     * Format percentage values with locale-aware formatting
     * @param amount - The percentage value in 0-100 range (e.g., 25 for 25%, NOT 0.25)
     * @param decimals - Number of decimal places (defaults to 2)
     * @returns Locale-formatted percentage string (e.g., "25.00%" or "25,00 %" depending on locale)
     */
    formatPercent: (amount: number | null | undefined, decimals: number = 2) => {
      if (amount === null || amount === undefined) {
        return "—"
      }
      // Intl.NumberFormat expects 0-1 range, so divide by 100
      return new Intl.NumberFormat(language, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(amount / 100)
    },

    /**
     * Format currency values in abbreviated format for charts (e.g., "$50K", "$1.2M")
     * Uses Intl.NumberFormat with compact notation for locale-aware abbreviations
     * @param value - The amount to format
     * @returns Abbreviated currency string
     */
    formatCurrencyShort: (value: number | null | undefined) => {
      if (value === null || value === undefined) {
        return "—"
      }
      
      return new Intl.NumberFormat(language, {
        notation: 'compact',
        compactDisplay: 'short',
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 1,
      }).format(value)
    },

    /**
     * Format date as month and year (e.g., "Jan 2024")
     * @param date - The date to format
     * @param fallback - Fallback string for invalid dates
     */
    formatMonthYear: (date: Date | string | null | undefined, fallback?: string) => {
      return formatMonthYear(date, language, fallback)
    }
  }
}